import { type RefObject, useEffect, useRef } from "react";
import { useNestedMenuContext } from "./NestedMenuContext";
import {
  MenuPopout,
  type TypeMenuPopoutProps,
  type TypeMenuRootProps,
} from "../MenuRoot";
import { MenuToggleProvider } from "../MenuContext";
import { motion } from "motion/react";
import styled from "styled-components";
import { mergeRefs } from "@sproutsocial/seeds-react-utilities";

const StyledDiv = styled(motion.div)`
  overflow: hidden;
`;

/** How long to wait after closing before returning focus to the toggle. */
const RETURN_FOCUS_DELAY = 200;
/** How long to wait before retrying an attempt that was undone. */
const RETURN_FOCUS_RETRY_DELAY = 50;
/** Upper bound on retrying, so an unfocusable toggle cannot retry forever. */
const RETURN_FOCUS_BUDGET = 1000;

export const NestedMenuPopout = ({
  content,
  menuToggleElement,
  width,
  onClose,
  toggleElementRef,
  focusLockProps,
  ...popoutProps
}: Omit<TypeMenuPopoutProps, "children"> & {
  menuToggleElement: TypeMenuRootProps["menuToggleElement"];
  toggleElementRef: RefObject<HTMLElement>;
}) => {
  const { activeMenuContext, rootMenuContextProps, resetSelectedMenuPath } =
    useNestedMenuContext();

  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      clearTimeout(closeTimerRef.current);
      clearTimeout(retryTimerRef.current);
    },
    []
  );

  /**
   * Move focus back to the toggle, retrying until it takes effect.
   *
   * The popout's focus lock stays mounted for the length of the close
   * animation, and while it is mounted it traps focus: a focus() call landing
   * outside the menu is immediately pulled back in. On a slow machine the
   * first attempt can fall inside that window, and once the lock unmounts
   * focus is left on <body> with nothing to restore it. Retrying covers that
   * instead of lengthening the initial delay, which can only ever guess at how
   * long the animation took.
   *
   * An attempt that takes effect stops the retries, so this never keeps
   * pulling focus back from somewhere the user deliberately moved it.
   */
  const attemptReturnFocus = (deadline: number) => {
    const toggleElement = toggleElementRef.current;

    if (toggleElement) {
      if (document.activeElement === toggleElement) {
        return;
      }
      toggleElement.focus();
      if (document.activeElement === toggleElement) {
        return;
      }
    }

    if (Date.now() < deadline) {
      retryTimerRef.current = setTimeout(
        () => attemptReturnFocus(deadline),
        RETURN_FOCUS_RETRY_DELAY
      );
    }
  };

  return (
    <MenuPopout
      lockPlacement
      // Reset the selected menu path after the close animation completes
      onClose={() => {
        onClose?.();
        // Manually focus the toggle element and then reset state
        closeTimerRef.current = setTimeout(() => {
          attemptReturnFocus(Date.now() + RETURN_FOCUS_BUDGET);
          resetSelectedMenuPath?.();
        }, RETURN_FOCUS_DELAY);
      }}
      isOpen={!!rootMenuContextProps.isOpen}
      openMenu={rootMenuContextProps.openMenu}
      closeMenu={rootMenuContextProps.closeMenu}
      width={width}
      content={(props) => (
        <StyledDiv layout="position">
          {typeof content === "function" ? content(props) : content}
        </StyledDiv>
      )}
      focusLockProps={{
        crossFrame: false,
        ...focusLockProps,
      }}
      {...popoutProps}
    >
      {(toggleProps) => (
        <MenuToggleProvider
          // rootMenuContextProps overrides activeMenuContext since it handles open/close logic
          menuContext={{
            ...activeMenuContext,
            ...rootMenuContextProps,
            ...toggleProps,
            ref: mergeRefs<HTMLElement>(
              toggleProps.ref,
              toggleElementRef
            ) as RefObject<HTMLElement>,
          }}
        >
          {menuToggleElement}
        </MenuToggleProvider>
      )}
    </MenuPopout>
  );
};
